import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Check,
  Sparkles,
  TrendingUp,
  Users,
  Eye,
  Building2,
  Zap,
  ShieldCheck,
  Star,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  Car,
  Utensils,
  Wrench,
  Dumbbell,
  Scissors,
  HardHat,
  Menu,
  X,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542705959-878ca346eb20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxoaWdod2F5JTIwdHJhZmZpYyUyMG5pZ2h0JTIwbG9uZyUyMGV4cG9zdXJlfGVufDB8fHx8MTc4MTU5NTgzM3ww&ixlib=rb-4.1.0&q=85";
const NISSAN_LED = "/images/loimaan-laatuauto.jpg";
const LAKIKOSKI_LED = "/images/lakikoski.png";

const PHONE = "+358505531110";
const PHONE_PRETTY = "+358 50 553 1110";
const EMAIL = "juho@myyntidrive.fi";

const navLinks = [
  { id: "ratkaisu", label: "Ratkaisu" },
  { id: "esimerkit", label: "Esimerkit" },
  { id: "hinnasto", label: "Hinnasto" },
  { id: "vertailu", label: "Vertailu" },
  { id: "prosessi", label: "Näin toimii" },
  { id: "faq", label: "FAQ" },
  { id: "yhteys", label: "Yhteydenotto" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ============== NAV ==============
const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-[#1E2F44]/60" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <button
          onClick={() => scrollTo("top")}
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          <span className="relative inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#22D3EE] text-black font-black text-sm led-glow">
            9
          </span>
          <span className="font-display text-lg font-bold tracking-tight">
            Ysitien <span className="text-[#22D3EE] led-text-glow">LED</span>
          </span>
        </button>
        <nav className="hidden md:flex items-center gap-7 text-sm text-neutral-300">
          {navLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="hover:text-white transition-colors"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${PHONE}`}
            data-testid="nav-call"
            className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white transition-colors"
          >
            <Phone className="h-4 w-4 text-[#22D3EE]" /> {PHONE_PRETTY}
          </a>
          <button
            onClick={() => scrollTo("yhteys")}
            data-testid="nav-cta"
            className="inline-flex items-center gap-2 rounded-md bg-[#22D3EE] px-4 py-2 text-sm font-semibold text-black hover:bg-[#67E8F9] transition-all"
          >
            Varaa paikka <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <button
          className="md:hidden text-white"
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-mobile-toggle"
          aria-label="Avaa valikko"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-black/95 border-t border-[#1E2F44]/60 px-6 py-4 space-y-3">
          {navLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => {
                setOpen(false);
                scrollTo(l.id);
              }}
              className="block w-full text-left text-neutral-200"
              data-testid={`nav-mobile-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <a
            href={`tel:${PHONE}`}
            className="flex items-center gap-2 text-[#22D3EE] mt-2"
          >
            <Phone className="h-4 w-4" /> {PHONE_PRETTY}
          </a>
        </div>
      )}
    </header>
  );
};

// ============== HERO ==============
const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  return (
    <section id="top" className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden">
      <motion.div
        style={{ y }}
        aria-hidden
        className="absolute inset-0 -z-10"
      >
        <img
          src={HERO_IMG}
          alt="9-tien liikenne yöllä"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07111F]/80 via-[#07111F]/92 to-[#07111F]" />
        <div className="absolute inset-0 bg-ambient" />
      </motion.div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 relative">
        <Reveal>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#22D3EE]/30 bg-[#22D3EE]/5 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-[#67E8F9]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22D3EE] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22D3EE]" />
            </span>
            LED-mainonta Loimaalla · 9-tien varrella
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h1 className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] text-white">
            Näy <span className="text-accent-soft hero-underline">tuhansille autoilijoille</span>
            <br className="hidden sm:block" /> 9-tien varrella
          </h1>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-neutral-300">
            LED-mainonta paikallisille ja valtakunnallisille yrityksille. Kasvata
            tunnettuutta, ohjaa asiakkaita liikkeeseesi ja pysy ihmisten mielessä joka
            päivä — alkaen{" "}
            <span className="text-white font-semibold">400 € + alv</span>.
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={() => scrollTo("yhteys")}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-md bg-[#22D3EE] px-6 py-4 text-sm font-bold text-black transition-all hover:bg-[#67E8F9] led-glow pulse-glow"
            >
              Varaa mainospaikka
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollTo("hinnasto")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-6 py-4 text-sm font-semibold text-white transition-all hover:bg-white/10"
            >
              Katso hinnasto
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.4}>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#F1C46A]">
            <Sparkles className="h-4 w-4" />
            Mainospaikkoja on rajallisesti — varaa näkyvyys ennen kilpailijoitasi.
          </div>
        </Reveal>

        <Reveal delay={0.5}>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Eye, k: "Tuhansia", v: "autoilijoita päivittäin" },
              { icon: MapPin, k: "Loimaa", v: "9-tien varrella" },
              { icon: Zap, k: "Nopea", v: "käytössä muutamassa päivässä" },
              { icon: TrendingUp, k: "500 €/kk", v: "12 kk paketissa" },
            ].map((s, i) => (
              <div
                key={i}
                className="rounded-xl border border-[#1E2F44] bg-[#0D1B2A]/60 p-4 backdrop-blur"
                data-testid={`hero-stat-${i}`}
              >
                <s.icon className="h-5 w-5 text-[#22D3EE]" />
                <div className="mt-3 font-display text-xl font-semibold">{s.k}</div>
                <div className="text-xs text-neutral-400">{s.v}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ============== TRUST MARQUEE ==============
const TrustStrip = () => {
  const items = [
    "Ravintolat", "Kahvilat", "Autokorjaamot", "Rengasliikkeet",
    "Rakennusfirmat", "Remonttifirmat", "LVI-yritykset", "Sähköliikkeet",
    "Parturit", "Kauneushoitolat", "Kuntosalit", "Tapahtumat",
    "Kivijalkaliikkeet", "Ketjut & brändit", "Valtakunnalliset toimijat",
    "Paikalliset palvelut", "Uudet yritykset",
  ];
  const list = [...items, ...items];
  return (
    <section className="py-10 border-y border-[#1E2F44]/60 bg-black/40 overflow-hidden">
      <div className="text-center text-xs uppercase tracking-[0.25em] text-neutral-500">
        Sopii kymmenille toimialoille
      </div>
      <div className="mt-6 marquee">
        {list.map((t, i) => (
          <span
            key={i}
            className="text-neutral-400 text-sm md:text-base font-medium whitespace-nowrap"
          >
            {t} <span className="text-[#22D3EE] mx-3">●</span>
          </span>
        ))}
      </div>
    </section>
  );
};

// ============== SECTION HEADING ==============
const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#22D3EE]">
    <span className="h-px w-8 bg-[#22D3EE]" />
    {children}
  </div>
);

const SectionTitle = ({ eyebrow, title, subtitle, center }) => (
  <Reveal>
    <div className={center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-neutral-400 text-base sm:text-lg leading-relaxed">{subtitle}</p>
      )}
    </div>
  </Reveal>
);

// ============== PROBLEM ==============
const Problem = () => {
  const items = [
    { t: "Ihmiset eivät tiedä yrityksesi olemassaolosta", d: "Vaikka palvelu olisi hyvä, asiakkaat eivät päädy luoksesi jos eivät tiedä sinusta." },
    { t: "Some-mainokset hukkuvat hetkessä", d: "Feedissä mainos näkyy sekunteja — ja seuraavana päivänä se on jo unohdettu." },
    { t: "Näkyvyys jää kilpailijoille", d: "Kilpailija näkyy parhaalla paikalla joka päivä, sinä et missään." },
    { t: "Tarjoukset eivät tavoita oikeita ihmisiä", d: "Hyvä kampanja menee hukkaan, jos kohderyhmäsi ei näe sitä." },
    { t: "Tarvitset näkyvyyttä juuri oikealla alueella", d: "Ei valtakunnan median ylihintaa — vain kohdennettua huomiota oikeassa paikassa." },
    { t: "Asiakkaan pitää muistaa sinut tarpeen syntyessä", d: "Päätös tehdään silloin, kun nimi on jo mielessä — toistolla pääset sinne." },
  ];
  return (
    <section id="ongelma" className="py-24 md:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionTitle
          eyebrow="Ongelma"
          title="Paikallinen yritys jää usein näkymättömäksi — vaikka asiakkaat ovat lähellä."
          subtitle="Tutut haasteet, joiden kanssa yrittäjät painivat joka kuukausi."
        />
        <Stagger className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => (
            <Item
              key={i}
              className="group relative rounded-2xl border border-[#1E2F44] bg-[#0D1B2A] p-6 transition-all hover:-translate-y-1 hover:border-[#22D3EE]/40"
            >
              <div className="absolute -top-3 -left-3 inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#22D3EE] text-black font-bold text-sm led-glow">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-display text-lg font-semibold leading-snug">{it.t}</h3>
              <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{it.d}</p>
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

// ============== SOLUTION ==============
const Solution = () => {
  const points = [
    "Yrityksesi näkyy 9-tien liikenteelle joka päivä",
    "Mainos toistuu jatkuvasti — toisto rakentaa muistijälkeä",
    "LED-pinta erottuu ja herättää huomion sekunneissa",
    "Sopii sekä tunnettuuden kasvattamiseen että kampanjoihin",
    "Toimii sesongeissa, avajaisissa ja tarjouksissa",
    "Tehokas niin paikallisille kuin valtakunnallisille yrityksille — kun yleisösi kulkee tämän tien kautta",
  ];
  return (
    <section id="ratkaisu" className="py-24 md:py-28 bg-[#0A1729]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-2 rounded-3xl bg-[#22D3EE]/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-2xl border border-[#1E2F44]">
              <img
                src={NISSAN_LED}
                alt="Ysitien LED-taulu Loimaalla — Loimaan Laatuauto & Nissan mainos"
                className="w-full h-[460px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07111F]/85 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
                <Badge className="bg-[#22D3EE] text-[#07111F] hover:bg-[#67E8F9]">Aito mainos</Badge>
                <span className="text-sm text-white/90">Loimaan Laatuauto · 9-tien LED-taulu</span>
              </div>
            </div>
          </div>
        </Reveal>

        <div>
          <SectionTitle
            eyebrow="Ratkaisu"
            title="Ysitien LED — jatkuvaa näkyvyyttä 9-tien arjessa"
            subtitle="Emme myy yksittäistä mainosta. Myymme paikan, jossa yrityksesi nimi rakentaa muistijälkeä joka päivä — siellä, missä asiakkaasi ja brändisi yleisö oikeasti liikkuvat."
          />
          <Stagger className="mt-8 space-y-3">
            {points.map((p, i) => (
              <Item key={i} className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#22D3EE]/15 text-[#22D3EE]">
                  <Check className="h-4 w-4" />
                </span>
                <span className="text-neutral-200">{p}</span>
              </Item>
            ))}
          </Stagger>
          <Reveal delay={0.2}>
            <button
              onClick={() => scrollTo("hinnasto")}
              data-testid="solution-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#22D3EE] px-6 py-3.5 font-bold text-black hover:bg-[#67E8F9] transition-all led-glow"
            >
              Katso hinnasto <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ============== WHY LED ==============
const WhyLed = () => {
  const benefits = [
    { icon: Eye, t: "Näkyy oikeassa ympäristössä", d: "Ihmiset näkevät mainoksen siellä, missä he liikkuvat — ei satunnaisessa feedissä." },
    { icon: Users, t: "Tavoittaa 9-tien liikennettä", d: "9-tien autoilijat — niin paikalliset kuin pitkän matkan kulkijat — ovat juuri sinun potentiaalisia asiakkaitasi." },
    { icon: TrendingUp, t: "Rakentaa muistijälkeä", d: "Toistuva näkyvyys jää mieleen ja tukee yrityksen tunnettuutta." },
    { icon: ShieldCheck, t: "Vahvistaa uskottavuutta", d: "LED-taulun yritys vaikuttaa vakavasti otettavalta ja vahvalta toimijalta." },
    { icon: Zap, t: "Tarjous- ja brändimainontaan", d: "Toimii sekä nopeisiin kampanjoihin että pitkäkestoiseen brändityöhön." },
    { icon: Sparkles, t: "Tavoittaa myös ei-some-kuluttajat", d: "Iso osa kohderyhmästäsi ei vietä aikaa somessa — täällä tavoitat heidät." },
  ];
  return (
    <section className="py-24 md:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionTitle
          center
          eyebrow="Miksi LED-mainonta toimii"
          title="Realistinen näkyvyys, joka tukee yritystäsi joka päivä"
          subtitle="LED-mainonta ei ole taikatemppu, vaan jatkuva muistutus oikealle yleisölle oikeassa paikassa."
        />
        <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {benefits.map((b, i) => (
            <Item
              key={i}
              className="group rounded-2xl border border-[#1E2F44] bg-[#0D1B2A] p-6 transition-all hover:-translate-y-1 hover:border-[#22D3EE]/40"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/20 transition-all group-hover:scale-110">
                <b.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold">{b.t}</h3>
              <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{b.d}</p>
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

// ============== EXAMPLES (real customer ads) ==============
const Examples = () => {
  const examples = [
    {
      img: NISSAN_LED,
      tag: "Autoala",
      title: "Loimaan Laatuauto × Nissan",
      body:
        "Autoliikkeen kampanjamainos: Nissan Qashqai näkyvästi esillä, hinta ja korkotieto suoraan ohikulkijoille — huomio kiinnittyy sekunneissa.",
    },
    {
      img: LAKIKOSKI_LED,
      tag: "Palveluala",
      title: "Lakikoski · Lakiasiaintoimisto",
      body:
        "Selkeä brändimainos: yrityksen nimi, toimiala ja lupaus ”Sinun puolellasi” — juuri sitä muistijälkeä, jonka LED-mainonta tuo.",
    },
  ];
  return (
    <section id="esimerkit" className="py-24 md:py-28 bg-[#0A1729]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionTitle
          center
          eyebrow="Aitoja mainoksia 9-tieltä"
          title="Näin yrityksesi voisi näkyä"
          subtitle="Aidot esimerkit LED-taulultamme — omalta ja asiakkaidemme mainoksilta 9-tien varrelta Loimaasta."
        />
        <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {examples.map((ex, i) => (
            <Item
              key={i}
              className="group rounded-2xl overflow-hidden border border-[#1E2F44] bg-[#0D1B2A] transition-all hover:-translate-y-1 hover:border-[#22D3EE]/40"
            >
              <div className="relative overflow-hidden">
                <img
                  src={ex.img}
                  alt={ex.title}
                  className="w-full h-[340px] md:h-[420px] object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07111F]/70 via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-[#22D3EE] text-[#07111F] hover:bg-[#67E8F9]">{ex.tag}</Badge>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-semibold">{ex.title}</h3>
                <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{ex.body}</p>
              </div>
            </Item>
          ))}
        </Stagger>

        <Reveal delay={0.15}>
          <div className="mt-10 text-center">
            <button
              onClick={() => scrollTo("yhteys")}
              data-testid="examples-cta"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#E0B14C] to-[#F1C46A] px-6 py-3.5 font-bold text-[#1A1206] hover:brightness-110 transition-all shadow-lg shadow-[#E0B14C]/30"
            >
              Varaa mainospaikkasi <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ============== PRICING ==============
const PricingCard = ({
  tier, price, sub, badge, popular, features, ctaLabel, ctaTestId, onClick, icon: Icon,
}) => (
  <div
    className={`relative rounded-2xl p-8 transition-all ${
      popular
        ? "border-2 border-[#E0B14C] bg-gradient-to-b from-[#0F2238] to-[#0B1A2A] gold-glow scale-[1.02] lg:scale-[1.05]"
        : "border border-[#1E2F44] bg-[#0D1B2A] hover:-translate-y-1 hover:border-[#2A4060]"
    }`}
  >
    {popular && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#E0B14C] to-[#F1C46A] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#1A1206] shadow-lg shadow-[#E0B14C]/30">
          <Star className="h-3.5 w-3.5 fill-[#1A1206]" /> Suosituin
        </div>
      </div>
    )}
    <div className="flex items-center gap-3">
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${popular ? "bg-[#E0B14C] text-[#1A1206]" : "bg-[#0A1729] text-[#22D3EE] border border-[#1E2F44]"}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="font-display text-xl font-semibold">{tier}</div>
        {badge && (
          <div className="text-xs uppercase tracking-wider text-[#E0B14C] mt-0.5">{badge}</div>
        )}
      </div>
    </div>

    <div className="mt-6">
      <div className="flex items-baseline gap-2">
        <span className="font-display text-5xl font-bold text-white">{price}</span>
        <span className="text-[#93A3B8] text-sm">+ alv</span>
      </div>
      {sub && <div className={`mt-2 text-sm ${popular ? "text-[#F1C46A]" : "text-[#93A3B8]"}`}>{sub}</div>}
    </div>

    <ul className="mt-6 space-y-3">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-neutral-200">
          <Check className={`mt-0.5 h-4 w-4 shrink-0 ${popular ? "text-[#E0B14C]" : "text-[#22D3EE]/80"}`} />
          <span>{f}</span>
        </li>
      ))}
    </ul>

    <button
      onClick={onClick}
      data-testid={ctaTestId}
      className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3.5 font-semibold transition-all ${
        popular
          ? "bg-gradient-to-r from-[#E0B14C] to-[#F1C46A] text-[#1A1206] hover:brightness-110 shadow-lg shadow-[#E0B14C]/30"
          : "border border-[#22D3EE]/40 bg-[#22D3EE]/10 text-white hover:bg-[#22D3EE]/20"
      }`}
    >
      {ctaLabel} <ArrowRight className="h-4 w-4" />
    </button>
  </div>
);

const Pricing = () => {
  const go = (pkg) => {
    try {
      sessionStorage.setItem("ylp_pkg", pkg);
      window.dispatchEvent(new CustomEvent("ylp:pkg", { detail: pkg }));
    } catch (e) {
      // ignore storage errors
    }
    scrollTo("yhteys");
  };
  return (
    <section id="hinnasto" className="py-24 md:py-28 relative">
      <div className="absolute inset-0 -z-10 bg-ambient" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionTitle
          center
          eyebrow="Hinnasto"
          title="Valitse näkyvyytesi pituus"
          subtitle="Suosittelemme 12 kk pakettia — paras kuukausihinta ja pitkäkestoinen tunnettuus."
        />

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-5 items-start">
          <PricingCard
            icon={CalendarClock}
            tier="Viikkomainos"
            price="400 €"
            sub="1 viikko näkyvyyttä 9-tien varrella"
            features={[
              "Sopii tapahtumiin ja avajaisiin",
              "Lyhyt kampanja tai sesonkitarjous",
              "Nopea testi LED-mainonnasta",
              "Mainos näkyy koko viikon",
            ]}
            ctaLabel="Kysy viikon mainospaikkaa"
            ctaTestId="pricing-week-cta"
            onClick={() => go("week")}
          />

          <PricingCard
            icon={Star}
            popular
            tier="12 kk näkyvyys"
            badge="Paras pitkäaikaiseen näkyvyyteen"
            price="6000 €"
            sub="Vain 500 €/kk + alv · Eniten vastinetta rahalle"
            features={[
              "Jatkuva näkyvyys vuoden ympäri",
              "Paras kuukausihinta — säästät jopa 50 %",
              "Rakentaa brändiä ja tunnettuutta",
              "Mainos voidaan päivittää sesongeittain",
              "Pysyt asiakkaiden mielessä ennen ostotarvetta",
            ]}
            ctaLabel="Varaa 12 kk näkyvyys"
            ctaTestId="pricing-year-cta"
            onClick={() => go("year")}
          />

          <PricingCard
            icon={CalendarRange}
            tier="Kuukausimainos"
            price="1000 €"
            sub="1 kuukausi näkyvyyttä 9-tien varrella"
            features={[
              "Sesonkimainontaan ja kampanjoihin",
              "Uuden palvelun lanseeraukseen",
              "Hyvä keino testata kohderyhmää kuukauden ajan",
              "Mainos näkyy koko kuukauden",
            ]}
            ctaLabel="Kysy kuukauden mainospaikkaa"
            ctaTestId="pricing-month-cta"
            onClick={() => go("month")}
          />
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 rounded-2xl border border-[#E0B14C]/30 bg-gradient-to-r from-[#E0B14C]/8 to-[#22D3EE]/5 p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 text-[#E0B14C] text-sm font-semibold">
                  <Sparkles className="h-4 w-4" /> Vinkki yrittäjälle
                </div>
                <p className="mt-2 text-white text-lg md:text-xl max-w-3xl">
                  Kun yrityksesi näkyy samalla paikalla kuukaudesta toiseen, ihmiset alkavat
                  muistaa nimesi. <span className="text-[#E0B14C] font-semibold">12 kuukauden näkyvyys</span> on
                  paras valinta yritykselle, joka haluaa rakentaa tunnettuutta pitkäjänteisesti.
                </p>
              </div>
              <button
                onClick={() => go("year")}
                data-testid="pricing-year-cta-secondary"
                className="shrink-0 inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#E0B14C] to-[#F1C46A] px-6 py-3.5 font-bold text-[#1A1206] hover:brightness-110 transition-all shadow-lg shadow-[#E0B14C]/30"
              >
                Varaa 12 kk paketti <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ============== COMPARE ==============
const Compare = () => {
  const rows = [
    { label: "Mainosjakso", week: "1 viikko", month: "1 kuukausi", year: "12 kuukautta" },
    { label: "Kokonaishinta", week: "400 €", month: "1000 €", year: "6000 €" },
    { label: "Kuukausihinta", week: "~1 600 €", month: "1000 €", year: "500 €" },
    { label: "Sopii", week: "Tapahtuma / kampanja", month: "Sesonki / tarjous", year: "Jatkuva tunnettuus" },
    { label: "Brändin rakennus", week: "Heikko", month: "Kohtalainen", year: "Vahva" },
    { label: "Hinta / vastine", week: "Korkein €/kk", month: "Keskitaso", year: "Paras €/kk" },
  ];
  return (
    <section id="vertailu" className="py-24 md:py-28 bg-[#0A1729]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionTitle
          center
          eyebrow="Pakettien vertailu"
          title="Mikä paketti sopii sinulle?"
          subtitle="Jos haluat vain testata, viikko tai kuukausi voi riittää. Jos haluat jäädä ihmisten mieleen, 12 kk on järkevin valinta."
        />

        <Reveal>
          <div className="mt-12 overflow-x-auto rounded-2xl border border-[#1E2F44] bg-[#0D1B2A]">
            <table className="w-full min-w-[680px] text-left">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#93A3B8] border-b border-[#1E2F44]">
                  <th className="p-5"></th>
                  <th className="p-5">Viikko</th>
                  <th className="p-5">Kuukausi</th>
                  <th className="p-5 bg-[#E0B14C]/8 text-[#E0B14C]">12 kk · Suosituin</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    className="border-b border-[#1E2F44]/60 last:border-0 hover:bg-white/[0.02]"
                    data-testid={`compare-row-${i}`}
                  >
                    <td className="p-5 text-neutral-300 font-medium">{r.label}</td>
                    <td className="p-5 text-neutral-300">{r.week}</td>
                    <td className="p-5 text-neutral-300">{r.month}</td>
                    <td className="p-5 bg-[#E0B14C]/8 text-white font-semibold">{r.year}</td>
                  </tr>
                ))}
                <tr>
                  <td className="p-5"></td>
                  <td className="p-5"></td>
                  <td className="p-5"></td>
                  <td className="p-5 bg-[#E0B14C]/8">
                    <button
                      onClick={() => scrollTo("yhteys")}
                      data-testid="compare-cta"
                      className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#E0B14C] to-[#F1C46A] px-4 py-2.5 text-sm font-bold text-[#1A1206] hover:brightness-110 transition-all shadow-md shadow-[#E0B14C]/30"
                    >
                      Varaa 12 kk <ArrowRight className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ============== PROCESS ==============
const Process = () => {
  const steps = [
    { t: "Ota yhteyttä", d: "Kerro yrityksesi ja millaista näkyvyyttä haet." },
    { t: "Valitaan sopiva jakso", d: "Viikko, kuukausi tai 12 kk — sen mukaan mihin tähtäät." },
    { t: "Toimita mainosaineisto", d: "Lähetä valmis mainoskuva tai -viesti — LED-taulu on suunniteltu nopeaan ymmärrettävyyteen." },
    { t: "Mainos julkaistaan", d: "Yrityksesi saa näkyvyyttä 9-tien LED-taululla Loimaalla." },
    { t: "Näkyvyys jatkuu", d: "Etenkin 12 kk paketissa pysyt asiakkaiden mielessä jatkuvasti." },
  ];
  return (
    <section id="prosessi" className="py-24 md:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionTitle
          center
          eyebrow="Näin se toimii"
          title="Viidestä vaiheesta näkyväksi"
          subtitle="Selkeä prosessi — me hoidamme tekniikan, sinä keskityt liiketoimintaan."
        />
        <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
          {steps.map((s, i) => (
            <Item key={i} className="relative">
              <div className="rounded-2xl border border-[#1E2F44] bg-[#0D1B2A] p-6 h-full transition-all hover:-translate-y-1 hover:border-[#22D3EE]/40">
                <div className="font-display text-4xl font-bold text-[#22D3EE] led-text-glow">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{s.d}</p>
              </div>
              {i < steps.length - 1 && (
                <ArrowRight className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 text-[#22D3EE]/40 h-5 w-5" />
              )}
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

// ============== INDUSTRIES ==============
const Industries = () => {
  const list = [
    { icon: Utensils, t: "Ravintola & kahvila", d: "Mainosta lounasta, viikonlopputarjousta tai uutta menuuta ohikulkijoille." },
    { icon: Wrench, t: "Autokorjaamo & rengasliike", d: "Muistuta katsastushuolloista, rengasvaihdoista ja kausipalveluista." },
    { icon: Dumbbell, t: "Kuntosali", d: "Houkuttele uusia jäseniä kampanjalla tai ilmaisella tutustumiskerralla." },
    { icon: Scissors, t: "Kauneushoitola & parturi", d: "Nosta esiin ajanvaraus, lahjakortit tai sesonkihoidot." },
    { icon: HardHat, t: "Rakennus & remontti", d: "Kasvata tunnettuutta alueella ja jää mieleen ennen kuin tarve syntyy." },
    { icon: Building2, t: "Valtakunnalliset brändit & ketjut", d: "Vahvista brändin näkyvyyttä 9-tien varrella — ohikulkijat näkevät logosi joka päivä." },
  ];
  return (
    <section className="py-24 md:py-28 bg-[#0A1729]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionTitle
          center
          eyebrow="Esimerkkikäyttökohteet"
          title="Toimii eri toimialoilla ja eri kokoisille yrityksille"
          subtitle="Olitpa paikallinen kivijalkaliike tai valtakunnallinen brändi — jos asiakkaasi tai bränditarinasi kulkee 9-tien kautta, LED-mainos tukee yritystäsi joka päivä."
        />
        <Stagger className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((it, i) => (
            <Item
              key={i}
              className="group rounded-2xl border border-[#1E2F44] bg-[#0D1B2A] p-6 transition-all hover:-translate-y-1 hover:border-[#22D3EE]/40"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/20 transition-all group-hover:scale-110">
                  <it.icon className="h-6 w-6" />
                </span>
                <h3 className="font-display text-lg font-semibold">{it.t}</h3>
              </div>
              <p className="mt-4 text-sm text-neutral-400 leading-relaxed">{it.d}</p>
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

// ============== FAQ ==============
const FAQ = () => {
  const faqs = [
    { q: "Paljonko LED-mainonta maksaa?", a: "Viikko 400 € + alv, kuukausi 1 000 € + alv ja 12 kk 6 000 € + alv (eli vain 500 €/kk). 12 kk paketti on edullisin vaihtoehto kuukausitasolla ja paras pitkäkestoiseen näkyvyyteen." },
    { q: "Mitä 12 kk LED-mainos sisältää?", a: "Mainospaikan 9-tien LED-taulusta vuoden ajan. Mainos näkyy ohi ajavalle liikenteelle ja rakentaa tunnettuutta jatkuvasti." },
    { q: "Voinko ostaa mainoksen vain viikoksi?", a: "Kyllä. Viikon paketti 400 € + alv sopii erityisesti tapahtumiin, avajaisiin, lyhyihin kampanjoihin ja nopeaan testiin." },
    { q: "Kenelle LED-mainonta sopii?", a: "Sekä paikallisille että valtakunnallisille yrityksille: ravintolat, kahvilat, autokorjaamot, rakennus- ja remonttifirmat, kuntosalit, kauneushoitolat, kivijalkaliikkeet, ketjut ja brändit — kaikille, joiden asiakkaat tai kohderyhmä kulkee 9-tien kautta." },
    { q: "Näkyykö mainos 9-tien liikenteelle?", a: "Kyllä. LED-taulu sijaitsee Loimaalla 9-tien varrella ja tavoittaa autoilijat aamusta myöhäisiltaan asti." },
    { q: "Voinko vaihtaa mainosta kampanjan aikana?", a: "Pidemmissä paketeissa mainoksen voi päivittää esimerkiksi sesongin tai kampanjan mukaan. Sovitaan käytännöt yhteydenoton yhteydessä." },
    { q: "Miten nopeasti mainos saadaan näkyviin?", a: "Useimmiten muutamassa arkipäivässä yhteydenoton jälkeen, kun mainosaineisto ja aikataulu on sovittu." },
    { q: "Miksi 12 kk näkyvyys kannattaa?", a: "Toisto rakentaa muistijälkeä. Kun yrityksesi näkyy samalla paikalla kuukaudesta toiseen, ihmiset alkavat muistaa nimesi ennen kuin tarve syntyy — silloin valitaan sinut." },
    { q: "Miten varaan mainospaikan?", a: "Täytä lomake tällä sivulla tai soita / lähetä sähköpostia. Otamme yhteyttä yleensä saman arkipäivän aikana." },
  ];
  return (
    <section id="faq" className="py-24 md:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionTitle
          center
          eyebrow="Usein kysytyt kysymykset"
          title="Vastauksia ennen yhteydenottoa"
        />
        <Reveal>
          <div className="mt-12 max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="rounded-xl border border-[#1E2F44] bg-[#0D1B2A] px-5 data-[state=open]:border-[#22D3EE]/40"
                >
                  <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-neutral-400 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 text-center">
            <button
              onClick={() => scrollTo("yhteys")}
              data-testid="faq-cta"
              className="inline-flex items-center gap-2 rounded-md bg-[#22D3EE] px-6 py-3.5 font-bold text-black hover:bg-[#67E8F9] transition-all led-glow"
            >
              Varaa mainospaikka <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ============== CONTACT ==============
const Contact = () => {
  const initial =
    typeof window !== "undefined"
      ? sessionStorage.getItem("ylp_pkg") || "year"
      : "year";
  const [pkg, setPkg] = useState(initial);
  useEffect(() => {
    const onPkg = (e) => setPkg(e.detail);
    window.addEventListener("ylp:pkg", onPkg);
    return () => window.removeEventListener("ylp:pkg", onPkg);
  }, []);
  return (
    <section id="yhteys" className="py-24 md:py-28 bg-[#0A1729] relative overflow-hidden">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[#22D3EE]/10 blur-3xl" aria-hidden />
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
        <div>
          <SectionTitle
            eyebrow="Yhteydenotto"
            title="Varaa mainospaikka — vastaamme nopeasti."
            subtitle="Kerro yrityksesi ja millaista näkyvyyttä haet. Olemme yhteydessä yleensä saman arkipäivän aikana."
          />
          <div className="mt-8 space-y-4">
            <a
              href={`tel:${PHONE}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-4 rounded-xl border border-[#1E2F44] bg-[#0D1B2A] p-4 hover:border-[#22D3EE]/40 transition-all"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/20">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-400">Soita</div>
                <div className="font-display text-lg text-white">{PHONE_PRETTY}</div>
              </div>
            </a>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 rounded-xl border border-[#1E2F44] bg-[#0D1B2A] p-4 hover:border-[#22D3EE]/40 transition-all"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/20">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-400">Sähköposti</div>
                <div className="font-display text-lg text-white">{EMAIL}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 rounded-xl border border-[#1E2F44] bg-[#0D1B2A] p-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/20">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-400">Sijainti</div>
                <div className="font-display text-lg text-white">Loimaa · 9-tien varrella</div>
              </div>
            </div>
          </div>

          <div className="mt-8 rounded-xl border border-[#E0B14C]/30 bg-[#E0B14C]/8 p-5">
            <div className="flex items-start gap-3">
              <CalendarDays className="h-5 w-5 text-[#E0B14C] mt-0.5" />
              <div>
                <div className="font-display text-base font-semibold text-white">
                  Mainospaikkoja on rajallisesti
                </div>
                <p className="mt-1 text-sm text-neutral-300">
                  Varaa näkyvyys ennen kilpailijoitasi. Jokainen LED-taulu mahtuu vain
                  rajatun määrän mainostajia kerrallaan.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <ContactForm key={pkg} defaultPackage={pkg} />
        </Reveal>
      </div>
    </section>
  );
};

// ============== FOOTER ==============
const Footer = () => (
  <footer className="border-t border-[#1E2F44]/60 bg-black">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-8">
      <div>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#22D3EE] text-black font-black text-sm led-glow">
            9
          </span>
          <span className="font-display text-lg font-bold">
            Ysitien <span className="text-[#22D3EE]">LED</span>
          </span>
        </div>
        <p className="mt-4 text-sm text-neutral-400 max-w-xs">
          LED-mainonta paikallisille ja valtakunnallisille yrityksille 9-tien varrella
          Loimaalla. Näy tuhansille autoilijoille joka päivä.
        </p>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500">Yhteystiedot</div>
        <div className="mt-4 space-y-2 text-sm">
          <a href={`tel:${PHONE}`} className="block text-neutral-200 hover:text-[#22D3EE]">
            <Phone className="inline h-4 w-4 mr-2 text-[#22D3EE]" /> {PHONE_PRETTY}
          </a>
          <a href={`mailto:${EMAIL}`} className="block text-neutral-200 hover:text-[#22D3EE]">
            <Mail className="inline h-4 w-4 mr-2 text-[#22D3EE]" /> {EMAIL}
          </a>
          <div className="text-neutral-200">
            <MapPin className="inline h-4 w-4 mr-2 text-[#22D3EE]" /> Loimaa, Suomi
          </div>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500">Navigointi</div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
          {navLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-left text-neutral-300 hover:text-[#22D3EE]"
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
    </div>
    <div className="border-t border-[#1E2F44]/60">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
        <div>© {new Date().getFullYear()} Ysitien LED. Kaikki oikeudet pidätetään.</div>
        <div className="flex items-center gap-4">
          <RouterLink
            to="/tietosuoja"
            data-testid="footer-privacy"
            className="text-neutral-400 hover:text-[#22D3EE]"
          >
            Tietosuojaseloste
          </RouterLink>
          <RouterLink
            to="/kayttoehdot"
            data-testid="footer-terms"
            className="text-neutral-400 hover:text-[#22D3EE]"
          >
            Käyttöehdot
          </RouterLink>
          <span className="text-neutral-600">·</span>
          <span>LED-mainonta · 9-tie · Loimaa</span>
        </div>
      </div>
    </div>
  </footer>
);

// ============== STICKY MOBILE CTA ==============
const StickyMobileCTA = () => (
  <div
    data-testid="sticky-mobile-cta"
    className="md:hidden fixed inset-x-0 bottom-0 z-[60] safe-bottom px-4 pt-3"
  >
    <div className="rounded-2xl border border-[#1E2F44] bg-black/85 backdrop-blur-xl p-3 flex items-center gap-3 shadow-[0_-10px_40px_rgba(0,0,0,0.6)]">
      <a
        href={`tel:${PHONE}`}
        className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-[#22D3EE]"
        aria-label="Soita"
        data-testid="sticky-call"
      >
        <Phone className="h-5 w-5" />
      </a>
      <button
        onClick={() => scrollTo("yhteys")}
        data-testid="sticky-cta-btn"
        className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#22D3EE] py-3.5 font-bold text-black led-glow"
      >
        Varaa mainospaikka <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  </div>
);

// ============== SEO ==============
const SEO = () => {
  useEffect(() => {
    document.title = "LED-mainonta 9-tien varrella | Ysitien LED";
    const upsert = (name, content, prop = false) => {
      const sel = prop ? `meta[property='${name}']` : `meta[name='${name}']`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (prop) el.setAttribute("property", name);
        else el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    upsert(
      "description",
      "Näy tuhansille autoilijoille 9-tien varrella. LED-mainospaikat paikallisille ja valtakunnallisille yrityksille alk. 400 € + alv. Suosituin 12 kk näkyvyys 6000 € + alv."
    );
    upsert("keywords", "LED-mainonta, LED-mainos, LED-mainospaikka, LED-mainostaulu, mainonta 9-tiellä, mainospaikka 9-tien varrella, tienvarsimainonta, ulkomainonta yritykselle, paikallinen mainonta, valtakunnallinen mainonta, brändimainonta, LED-mainonta Loimaa, ulkomainonta Loimaa, ulkomainonta Varsinais-Suomi, ulkomainonta Turku, ulkomainonta Sastamala");
    upsert("og:title", "LED-mainonta 9-tien varrella | Ysitien LED", true);
    upsert("og:description", "Näy tuhansille autoilijoille 9-tien varrella. LED-mainospaikat paikallisille ja valtakunnallisille yrityksille alk. 400 € + alv.", true);
    upsert("og:type", "website", true);
    upsert("theme-color", "#22D3EE");
  }, []);
  return null;
};

export default function Landing() {
  return (
    <div className="bg-[#07111F] text-white">
      <SEO />
      <Nav />
      <main>
        <Hero />
        <TrustStrip />
        <Problem />
        <Solution />
        <WhyLed />
        <Examples />
        <Pricing />
        <Compare />
        <Process />
        <Industries />
        <FAQ />
        <Contact />
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
